"use client";

/**
 * CookiesPage — statische uitleg-pagina + knop om voorkeuren te herzien.
 */

import { openConsentPreferences } from "../lib/consent";

interface Props {
  brandName: string;
  brandPhone: string;
  brandEmail?: string;
  lastUpdated?: string;
  /** Juridische entiteit achter het merk. Default = "Zwaluw Comfortsanitair
   *  B.V."; `null` = merk is zelf de tenaamstelling → geen "onderdeel van". */
  parentCompany?: string | null;
}

const COLOR = {
  ink: "#141C28",
  inkLight: "#3A3A36",
  muted: "#6E6A60",
  cream: "#F6F2EA",
  creamDark: "#EAE4D7",
  border: "#E4DED3",
  cta: "#FF6B1A",
  white: "#FFFFFF",
};

export default function CookiesPage({
  brandName,
  brandPhone,
  brandEmail = "info@badkamer-wijzer.nl",
  lastUpdated = "23 april 2026",
  parentCompany = "Zwaluw Comfortsanitair B.V.",
}: Props) {
  const parentSimple = parentCompany?.replace(/\s*B\.V\.$/, "") ?? null;
  const showParent =
    !!parentCompany && brandName !== parentCompany && brandName !== parentSimple;
  return (
    <main
      style={{
        minHeight: "100vh",
        background: COLOR.cream,
        fontFamily: "system-ui, -apple-system, sans-serif",
        color: COLOR.inkLight,
        lineHeight: 1.7,
      }}
    >
      <article style={{ maxWidth: 760, margin: "0 auto", padding: "56px 24px 72px" }}>
        <h1 style={s.h1}>Cookieverklaring {brandName}</h1>
        <p style={{ ...s.p, color: COLOR.muted, fontSize: 13 }}>
          <strong style={{ color: COLOR.ink }}>Laatst bijgewerkt:</strong> {lastUpdated}
        </p>

        <p style={s.p}>
          Op de website van {brandName} gebruiken wij cookies en vergelijkbare technieken. Hieronder
          leest u welke dat zijn, waarom we ze gebruiken en hoe u ze kunt beheren.
        </p>

        <div style={s.prefsCard}>
          <p style={{ ...s.p, fontWeight: 600, color: COLOR.ink, margin: "0 0 8px" }}>
            Uw huidige voorkeuren aanpassen
          </p>
          <p style={{ ...s.p, fontSize: 14, margin: "0 0 14px" }}>
            U kunt uw toestemming op elk moment wijzigen of intrekken.
          </p>
          <button onClick={openConsentPreferences} style={s.prefsBtn}>
            Cookievoorkeuren aanpassen
          </button>
        </div>

        <h2 style={s.h2}>1. Wat zijn cookies?</h2>
        <p style={s.p}>
          Cookies zijn kleine tekstbestanden die bij het bezoeken van een website op uw apparaat
          worden opgeslagen. Ze bevatten informatie die de website bij een volgend bezoek herkent
          (bijvoorbeeld voor taal-voorkeuren of login).
        </p>

        <h2 style={s.h2}>2. Welke cookies gebruiken wij?</h2>
        <p style={s.p}>Wij verdelen cookies in drie categorieën:</p>

        <h3 style={s.h3}>Noodzakelijk (altijd actief)</h3>
        <p style={s.p}>
          Deze zijn nodig om de website goed te laten werken. U kunt ze niet uitzetten. Ze bewaren
          geen persoonlijke informatie waarmee u te identificeren bent.
        </p>
        <ul style={s.ul}>
          <li><strong>Formulier-draft</strong> — tijdelijk opslaan van uw invoer (localStorage)</li>
          <li><strong>Cookie-voorkeur</strong> — uw gekozen voorkeuren</li>
          <li><strong>Security + sessies</strong> — bescherming tegen misbruik</li>
        </ul>

        <h3 style={s.h3}>Analyse (optioneel)</h3>
        <p style={s.p}>
          Hiermee meten we anoniem bezoekersgedrag om de site te verbeteren. Worden alleen geplaatst
          als u toestemming geeft.
        </p>
        <ul style={s.ul}>
          <li><strong>Google Tag Manager (GTM)</strong> — container voor analyse-tags</li>
          <li><strong>Google Analytics / GA4</strong> — bezoekstatistieken</li>
        </ul>

        <h3 style={s.h3}>Marketing (optioneel)</h3>
        <p style={s.p}>
          Hiermee kunnen we relevante advertenties tonen en de effectiviteit van campagnes meten.
          Worden alleen geplaatst na uw expliciete toestemming.
        </p>
        <ul style={s.ul}>
          <li><strong>Meta Pixel (Facebook)</strong> — retargeting via Facebook/Instagram</li>
          <li><strong>Google Ads Conversion</strong> — effectiviteit van Google-advertenties</li>
          <li><strong>Resend link tracking</strong> — klikken in bevestigingsmails (alleen bij mail-opens)</li>
        </ul>

        <h2 style={s.h2}>3. Consent Mode v2</h2>
        <p style={s.p}>
          Wij gebruiken Google Consent Mode v2. Dat betekent dat marketing- en analyse-cookies
          <em> pas</em> worden geladen nadat u toestemming heeft gegeven via onze cookiebanner. Tot
          die tijd blijven alleen noodzakelijke cookies actief.
        </p>

        <h2 style={s.h2}>4. Hoelang worden cookies bewaard?</h2>
        <p style={s.p}>
          Uw cookie-voorkeur wordt 12 maanden bewaard. Daarna vragen wij u opnieuw uw keuze te
          maken. Analytische en marketing-cookies hebben ieder eigen bewaartermijnen (meestal 30 dagen
          tot 2 jaar).
        </p>

        <h2 style={s.h2}>5. Hoe beheert u cookies?</h2>
        <p style={s.p}>
          Via de knop bovenaan deze pagina kunt u uw voorkeuren op elk moment aanpassen. U kunt
          cookies ook verwijderen via de instellingen van uw browser.
        </p>

        <h2 style={s.h2}>6. Contact</h2>
        <p style={s.p}>
          Vragen over deze cookieverklaring? Neem contact met ons op:
        </p>
        <p style={s.p}>
          <strong>{brandName}</strong>
          {showParent ? (
            <>
              <br />
              onderdeel van <strong>{parentCompany}</strong>
            </>
          ) : null}
          <br />
          Telefoon:{" "}
          <a href={`tel:${brandPhone.replace(/\s/g, "")}`} style={s.link}>
            {brandPhone}
          </a>
          <br />
          E-mail:{" "}
          <a href={`mailto:${brandEmail}`} style={s.link}>
            {brandEmail}
          </a>
        </p>
      </article>
    </main>
  );
}

const s: Record<string, React.CSSProperties> = {
  h1: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: "clamp(28px, 4vw, 40px)",
    fontWeight: 500,
    color: COLOR.ink,
    margin: "0 0 16px",
    letterSpacing: "-0.02em",
    lineHeight: 1.15,
  },
  h2: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: "clamp(20px, 2.2vw, 24px)",
    fontWeight: 500,
    color: COLOR.ink,
    margin: "36px 0 12px",
    letterSpacing: "-0.01em",
    lineHeight: 1.25,
  },
  h3: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: 18,
    fontWeight: 500,
    color: COLOR.ink,
    margin: "20px 0 10px",
  },
  p: {
    fontSize: 15.5,
    color: COLOR.inkLight,
    lineHeight: 1.7,
    margin: "0 0 14px",
  },
  ul: {
    fontSize: 15,
    color: COLOR.inkLight,
    lineHeight: 1.9,
    paddingLeft: 22,
    margin: "0 0 18px",
  },
  link: {
    color: COLOR.cta,
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
  prefsCard: {
    background: COLOR.creamDark,
    border: `1px solid ${COLOR.border}`,
    borderRadius: 12,
    padding: "18px 20px",
    margin: "24px 0 10px",
  },
  prefsBtn: {
    display: "inline-block",
    padding: "10px 22px",
    borderRadius: 8,
    border: "none",
    background: COLOR.cta,
    color: COLOR.white,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
};
